/**
 * Session de l'opérateur interne.
 *
 * Deux choix structurants, tous deux dictés par docs/06-revue-critique.md et par la nature de
 * l'outil :
 *
 *  1. **Les jetons sont opaques.** Aucun décodage, aucun claim, aucune date d'expiration lue
 *     dans le jeton lui-même : seule `expires_in`, annoncée par le serveur, est connue. Le rôle
 *     affiché vient du profil renvoyé par l'API, jamais d'un contenu de jeton — et il est de
 *     toute façon revérifié côté serveur à chaque opération.
 *  2. **Stockage en `sessionStorage`, pas en `localStorage`.** Le back-office voit tout le parc
 *     de tous les clients ; une session qui survit à la fermeture de l'onglet sur un poste
 *     partagé est un risque disproportionné pour le confort qu'elle apporte. Fermer l'onglet
 *     ferme la session.
 */

import type { OperateurInterne } from '@genviz/shared/dto';

import { api, brancherSession, ErreurApi } from '../api/client';
import type { ConnexionStaffReponse } from '../api/types';

const CLE_STOCKAGE = 'genviz.admin.session';

/** Marge de sécurité : on rafraîchit avant l'expiration annoncée, jamais après. */
const MARGE_EXPIRATION_MS = 60_000;

export interface EtatSession {
  operateur: OperateurInterne | null;
  jetonAcces: string | null;
  jetonRafraichissement: string | null;
  /** Instant d'expiration du jeton d'accès, en millisecondes epoch. */
  expireA: number | null;
}

const SESSION_VIDE: EtatSession = Object.freeze({
  operateur: null,
  jetonAcces: null,
  jetonRafraichissement: null,
  expireA: null,
});

let etat: EtatSession = SESSION_VIDE;
const auditeurs = new Set<() => void>();

/* -------------------------------------------------------------------------- */
/* Persistance                                                                 */
/* -------------------------------------------------------------------------- */

function relire(): EtatSession {
  try {
    const brut = window.sessionStorage.getItem(CLE_STOCKAGE);
    if (!brut) return SESSION_VIDE;
    const donnees = JSON.parse(brut) as Partial<EtatSession>;
    if (!donnees.jetonRafraichissement || !donnees.operateur) return SESSION_VIDE;
    return Object.freeze({
      operateur: donnees.operateur,
      jetonAcces: donnees.jetonAcces ?? null,
      jetonRafraichissement: donnees.jetonRafraichissement,
      expireA: donnees.expireA ?? null,
    });
  } catch {
    return SESSION_VIDE;
  }
}

function ecrire(nouvel: EtatSession): void {
  etat = Object.freeze(nouvel);
  try {
    if (etat.jetonRafraichissement === null) window.sessionStorage.removeItem(CLE_STOCKAGE);
    else window.sessionStorage.setItem(CLE_STOCKAGE, JSON.stringify(etat));
  } catch {
    // Stockage indisponible : la session reste valable pour la durée de vie de la page.
  }
  for (const auditeur of auditeurs) auditeur();
}

etat = relire();

/* -------------------------------------------------------------------------- */
/* Lecture et abonnement (compatibles `useSyncExternalStore`)                  */
/* -------------------------------------------------------------------------- */

export function lireSession(): EtatSession {
  return etat;
}

export function abonnerSession(auditeur: () => void): () => void {
  auditeurs.add(auditeur);
  return () => {
    auditeurs.delete(auditeur);
  };
}

export function estConnecte(): boolean {
  return etat.operateur !== null && etat.jetonRafraichissement !== null;
}

/* -------------------------------------------------------------------------- */
/* Cycle de vie                                                                */
/* -------------------------------------------------------------------------- */

function appliquerJetons(
  operateur: OperateurInterne,
  jetons: { access_token: string; refresh_token: string; expires_in: number },
): void {
  ecrire({
    operateur,
    jetonAcces: jetons.access_token,
    jetonRafraichissement: jetons.refresh_token,
    expireA: Date.now() + jetons.expires_in * 1000,
  });
}

/** Ouvre une session à partir de la réponse de connexion. */
export function ouvrirSession(reponse: ConnexionStaffReponse): void {
  appliquerJetons(reponse.operateur, reponse.jetons);
}

/** Met à jour le profil sans toucher aux jetons (rechargement de `GET /me`). */
export function majOperateur(operateur: OperateurInterne): void {
  if (etat.jetonRafraichissement === null) return;
  ecrire({ ...etat, operateur });
}

/**
 * Ferme la session. La révocation serveur est tentée mais n'est jamais bloquante : un opérateur
 * qui clique sur « Se déconnecter » doit être déconnecté localement même si le réseau est tombé.
 */
export async function fermerSession(): Promise<void> {
  const jeton = etat.jetonRafraichissement;
  ecrire(SESSION_VIDE);
  if (!jeton) return;
  try {
    await api.auth.deconnexion({ refresh_token: jeton });
  } catch {
    // Sans effet visible : la session locale est déjà détruite.
  }
}

/* -------------------------------------------------------------------------- */
/* Rafraîchissement                                                            */
/* -------------------------------------------------------------------------- */

let rafraichissementEnCours: Promise<boolean> | null = null;

/**
 * Renouvelle le jeton d'accès. Les appels concurrents partagent la même promesse : dix requêtes
 * qui se prennent un 401 en même temps ne doivent pas consommer dix jetons de rafraîchissement
 * rotatifs — le serveur y verrait un rejeu et révoquerait toutes les sessions.
 */
export function rafraichirSession(): Promise<boolean> {
  if (rafraichissementEnCours) return rafraichissementEnCours;

  const jeton = etat.jetonRafraichissement;
  const operateur = etat.operateur;
  if (!jeton || !operateur) return Promise.resolve(false);

  rafraichissementEnCours = (async () => {
    try {
      const reponse = await api.auth.rafraichir({ refresh_token: jeton });
      appliquerJetons(operateur, reponse.jetons);
      return true;
    } catch (cause) {
      // 401/403 : le jeton est mort, la session aussi. Une panne réseau, elle, ne doit pas
      // déconnecter l'opérateur : on garde la session et l'appelant réessaiera.
      if (cause instanceof ErreurApi && (cause.estAuthentification || cause.estInterdit)) {
        ecrire(SESSION_VIDE);
      }
      return false;
    } finally {
      rafraichissementEnCours = null;
    }
  })();

  return rafraichissementEnCours;
}

/** Délai avant le prochain rafraîchissement préventif, en ms. `null` si sans objet. */
export function delaiAvantRafraichissement(): number | null {
  if (etat.expireA === null || !estConnecte()) return null;
  return Math.max(5_000, etat.expireA - Date.now() - MARGE_EXPIRATION_MS);
}

/* -------------------------------------------------------------------------- */
/* Branchement du client HTTP                                                  */
/* -------------------------------------------------------------------------- */

brancherSession({
  jetonAcces: () => etat.jetonAcces,
  rafraichir: rafraichirSession,
  sessionPerdue: () => {
    if (estConnecte()) ecrire(SESSION_VIDE);
  },
});
